import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Building2,
    Moon,
    Sun,
    Network,
    FileText,
    Zap,
    LogIn,
    Radio,
    Shield,
    Database,
    Link2,
    LayoutGrid,
    ArrowDown,
    Waypoints,
} from 'lucide-react';
import { clsx } from 'clsx';
import { hasActiveSession } from '../utils/session';
import { OutrisSiteFooter } from '../components/OutrisSiteFooter';
import { LandingCaseMap } from '../components/LandingCaseMap';
import { LandingWorkbenchDemo } from '../components/LandingWorkbenchDemo';

const PORTAL_BASE = (import.meta.env.VITE_PORTAL_URL || 'https://portal.outris.com').replace(/\/$/, '');

function scrollToWorkbenchDemo() {
    document.getElementById('workbench-demo')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function readIsDarkTheme(): boolean {
    if (typeof document === 'undefined') return true;
    return document.documentElement.classList.contains('dark');
}

function applyTheme(isDark: boolean) {
    if (typeof document === 'undefined') return;
    if (isDark) {
        document.documentElement.classList.add('dark');
        localStorage.setItem('theme', 'dark');
    } else {
        document.documentElement.classList.remove('dark');
        localStorage.setItem('theme', 'light');
    }
}

// ── Interactive Background Particle & Node Canvas ──
const NetworkHeroCanvas: React.FC<{ isDark: boolean }> = ({ isDark }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let animationFrameId: number;
        let width = (canvas.width = canvas.parentElement?.clientWidth || window.innerWidth);
        let height = (canvas.height = canvas.parentElement?.clientHeight || window.innerHeight);

        const handleResize = () => {
            if (!canvas || !canvas.parentElement) return;
            width = canvas.width = canvas.parentElement.clientWidth;
            height = canvas.height = canvas.parentElement.clientHeight;
        };
        window.addEventListener('resize', handleResize);

        // Generate network nodes
        const nodeCount = Math.floor(Math.min(width, 1400) / 24);
        const nodes: Array<{
            x: number;
            y: number;
            vx: number;
            vy: number;
            radius: number;
            isHub: boolean;
            pulse: number;
        }> = [];

        for (let i = 0; i < nodeCount; i++) {
            const isHub = i % 6 === 0;
            nodes.push({
                x: Math.random() * width,
                y: Math.random() * height,
                vx: (Math.random() - 0.5) * 0.4,
                vy: (Math.random() - 0.5) * 0.4,
                radius: isHub ? Math.random() * 2 + 2.5 : Math.random() * 1.5 + 1.2,
                isHub,
                pulse: Math.random() * Math.PI * 2,
            });
        }

        // Mouse interaction
        let mouseX = -1000;
        let mouseY = -1000;
        const handleMouseMove = (e: MouseEvent) => {
            const rect = canvas.getBoundingClientRect();
            mouseX = e.clientX - rect.left;
            mouseY = e.clientY - rect.top;
        };
        const handleMouseLeave = () => {
            mouseX = -1000;
            mouseY = -1000;
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseleave', handleMouseLeave);

        const render = () => {
            ctx.clearRect(0, 0, width, height);

            const primaryColor = isDark ? '252, 175, 23' : '232, 158, 20';
            const cyanColor = isDark ? '34, 211, 238' : '14, 165, 233';
            const nodeBaseColor = isDark ? '255, 255, 255' : '30, 41, 59';

            for (let i = 0; i < nodes.length; i++) {
                const a = nodes[i];
                a.x += a.vx;
                a.y += a.vy;
                a.pulse += 0.03;

                if (a.x < 0 || a.x > width) a.vx *= -1;
                if (a.y < 0 || a.y > height) a.vy *= -1;

                const dxm = mouseX - a.x;
                const dym = mouseY - a.y;
                const distM = Math.sqrt(dxm * dxm + dym * dym);
                if (distM < 160) {
                    const force = (160 - distM) / 160;
                    a.x -= (dxm / distM) * force * 1.2;
                    a.y -= (dym / distM) * force * 1.2;
                }

                for (let j = i + 1; j < nodes.length; j++) {
                    const b = nodes[j];
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    const dist = Math.sqrt(dx * dx + dy * dy);

                    if (dist < 130) {
                        const alpha = (1 - dist / 130) * (isDark ? 0.22 : 0.14);
                        ctx.strokeStyle = a.isHub || b.isHub
                            ? `rgba(${primaryColor}, ${alpha * 1.5})`
                            : `rgba(${nodeBaseColor}, ${alpha})`;
                        ctx.lineWidth = a.isHub && b.isHub ? 1.2 : 0.6;
                        ctx.beginPath();
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                        ctx.stroke();

                        if (a.isHub && (i + j) % 3 === 0) {
                            const progress = (Math.sin(a.pulse) + 1) / 2;
                            const px = a.x + (b.x - a.x) * progress;
                            const py = a.y + (b.y - a.y) * progress;
                            ctx.fillStyle = `rgba(${cyanColor}, ${0.8 * (1 - dist / 130)})`;
                            ctx.beginPath();
                            ctx.arc(px, py, 1.8, 0, Math.PI * 2);
                            ctx.fill();
                        }
                    }
                }

                ctx.beginPath();
                ctx.arc(a.x, a.y, a.radius, 0, Math.PI * 2);
                if (a.isHub) {
                    ctx.fillStyle = `rgba(${primaryColor}, ${isDark ? 0.9 : 0.8})`;
                    ctx.shadowColor = `rgba(${primaryColor}, 0.5)`;
                    ctx.shadowBlur = 8;
                } else {
                    ctx.fillStyle = `rgba(${nodeBaseColor}, ${isDark ? 0.4 : 0.3})`;
                    ctx.shadowBlur = 0;
                }
                ctx.fill();
                ctx.shadowBlur = 0;
            }

            animationFrameId = requestAnimationFrame(render);
        };

        render();

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseleave', handleMouseLeave);
            cancelAnimationFrame(animationFrameId);
        };
    }, [isDark]);

    return (
        <canvas
            ref={canvasRef}
            className="absolute inset-0 pointer-events-none z-0 opacity-80"
        />
    );
};

export function Landing() {
    const navigate = useNavigate();
    const [isDarkTheme, setIsDarkTheme] = useState(() => readIsDarkTheme());
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        setIsLoggedIn(hasActiveSession());
    }, []);

    useEffect(() => {
        applyTheme(isDarkTheme);
    }, [isDarkTheme]);

    const toggleTheme = () => {
        setIsDarkTheme((prev) => !prev);
    };

    return (
        <div
            className={clsx(
                'min-h-screen relative overflow-x-hidden selection:bg-primary/30 flex flex-col justify-between',
                'bg-brand-page dark:bg-background text-foreground',
            )}
        >
            {/* Top Navigation Bar — logo left edge; actions + theme pill at right edge */}
            <header className="relative z-20 w-full px-3 sm:px-4 pt-6 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 shrink-0 rounded-xl bg-card border border-border/80 flex items-center justify-center shadow-xs">
                        <img
                            src="/brand/cryptoflow-icon-light.svg"
                            alt="CryptoFlow"
                            className="w-7 h-7 block dark:hidden"
                        />
                        <img
                            src="/brand/cryptoflow-icon-dark.svg"
                            alt="CryptoFlow"
                            className="w-7 h-7 hidden dark:block"
                        />
                    </div>
                    <div className="min-w-0">
                        <span className="font-bold text-lg tracking-tight text-foreground">CryptoFlow</span>
                        <span className="text-[11px] block font-mono text-muted-foreground uppercase tracking-wider">
                            Intelligence &amp; Forensics
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                    {isLoggedIn ? (
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-semibold text-xs hover:bg-brand-gold-hover transition-colors shadow-sm"
                        >
                            <LayoutGrid className="w-3.5 h-3.5" />
                            <span>Open Dashboard</span>
                        </button>
                    ) : (
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={scrollToWorkbenchDemo}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border/80 bg-card text-foreground font-semibold text-xs hover:bg-muted transition-colors"
                                data-testid="landing-see-sample-header"
                            >
                                <ArrowDown className="w-3.5 h-3.5 text-primary" />
                                <span>See a sample</span>
                            </button>
                            <button
                                onClick={() => navigate('/login')}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-semibold text-xs hover:bg-brand-gold-hover transition-colors shadow-sm"
                            >
                                <LogIn className="w-3.5 h-3.5" />
                                <span>Sign In</span>
                            </button>
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={toggleTheme}
                        role="switch"
                        aria-checked={isDarkTheme}
                        aria-label={isDarkTheme ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                        className="inline-flex h-9 w-[3.4rem] items-center rounded-full border border-border bg-card/80 backdrop-blur-sm p-1 transition-colors"
                    >
                        <span
                            className={clsx(
                                'flex h-6 w-6 items-center justify-center rounded-full border border-border transition-transform duration-200 shadow-xs',
                                isDarkTheme
                                    ? 'translate-x-6 bg-brand-ink'
                                    : 'translate-x-0 bg-brand-cream',
                            )}
                        >
                            {isDarkTheme ? (
                                <Moon className="w-3.5 h-3.5 text-foreground" />
                            ) : (
                                <Sun className="w-3.5 h-3.5 text-brand-ink" />
                            )}
                        </span>
                    </button>
                </div>
            </header>

            {/* Main Hero Showcase */}
            <main className="relative z-10 w-full py-12 flex-1 flex flex-col items-center justify-center">
                <NetworkHeroCanvas isDark={isDarkTheme} />

                {/* Soft hero wash — flat tint only (no decorative blur per lint:ui) */}
                <div
                    aria-hidden="true"
                    className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full bg-primary/8 dark:bg-primary/5 pointer-events-none"
                />

                <div className="relative z-10 space-y-6 max-w-5xl mx-auto px-6 text-center">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/25 shadow-xs">
                        <Radio className="w-3.5 h-3.5 text-primary animate-pulse" />
                        <span>Law Enforcement &amp; Cyber Forensics Gateway</span>
                    </div>

                    <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground leading-[1.15]">
                        Crypto Asset Tracing &amp;{' '}
                        <span className="text-primary">Fund Recovery</span>
                    </h1>

                    <p className="max-w-2xl mx-auto text-sm sm:text-base text-muted-foreground leading-relaxed">
                        Trace multi-hop blockchain transactions, bridge fiat P2P trails to target wallets, and compile structured evidence dossiers for investigation dockets.
                    </p>

                    {/* CTAs */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                        {isLoggedIn ? (
                            <button
                                onClick={() => navigate('/dashboard')}
                                className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-brand-gold-hover transition-all duration-200 shadow-md flex items-center justify-center gap-2"
                            >
                                <LayoutGrid className="w-4 h-4" />
                                <span>Open Investigation Dashboard</span>
                            </button>
                        ) : (
                            <>
                                <button
                                    onClick={() => navigate('/login')}
                                    className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:bg-brand-gold-hover transition-all duration-200 shadow-md flex items-center justify-center gap-2"
                                >
                                    <LogIn className="w-4 h-4" />
                                    <span>Sign In with Officer ID</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={scrollToWorkbenchDemo}
                                    className="w-full sm:w-auto px-6 py-3.5 rounded-xl border border-border/80 bg-card/80 backdrop-blur-sm text-foreground hover:bg-muted font-medium text-sm transition-all duration-200 shadow-xs flex items-center justify-center gap-2"
                                    data-testid="landing-see-sample"
                                >
                                    <ArrowDown className="w-4 h-4 text-primary" />
                                    <span>See a sample</span>
                                </button>
                                <button
                                    onClick={() => { window.location.href = `${PORTAL_BASE}/login?next=cryptoflow`; }}
                                    className="w-full sm:w-auto px-6 py-3.5 rounded-xl border border-border/80 bg-card/80 backdrop-blur-sm text-foreground hover:bg-muted font-medium text-sm transition-all duration-200 shadow-xs flex items-center justify-center gap-2"
                                >
                                    <Building2 className="w-4 h-4 text-primary" />
                                    <span>Outris SSO Login</span>
                                </button>
                            </>
                        )}
                    </div>

                    {/* Factual Registry Metrics */}
                    <div className="pt-8 grid grid-cols-1 sm:grid-cols-3 gap-3.5 max-w-2xl mx-auto text-left">
                        <div className="p-3.5 rounded-xl border border-border/70 bg-card/70 backdrop-blur-xs shadow-xs">
                            <div className="flex items-center justify-between text-muted-foreground mb-1">
                                <span className="text-[10px] font-semibold uppercase tracking-wider">Entity Registry</span>
                                <Database className="w-3.5 h-3.5 text-primary" />
                            </div>
                            <div className="text-xl font-bold font-mono text-foreground">36,400+</div>
                            <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium mt-0.5">Tagged Threat &amp; Exchange Wallets</div>
                        </div>

                        <div className="p-3.5 rounded-xl border border-border/70 bg-card/70 backdrop-blur-xs shadow-xs">
                            <div className="flex items-center justify-between text-muted-foreground mb-1">
                                <span className="text-[10px] font-semibold uppercase tracking-wider">Chain Coverage</span>
                                <Link2 className="w-3.5 h-3.5 text-blue-500" />
                            </div>
                            <div className="text-xl font-bold font-mono text-foreground">105+</div>
                            <div className="text-[11px] text-blue-600 dark:text-blue-400 font-medium mt-0.5">EVM, TRON, Solana &amp; Non-EVM</div>
                        </div>

                        <div className="p-3.5 rounded-xl border border-border/70 bg-card/70 backdrop-blur-xs shadow-xs">
                            <div className="flex items-center justify-between text-muted-foreground mb-1">
                                <span className="text-[10px] font-semibold uppercase tracking-wider">Case Organization</span>
                                <Shield className="w-3.5 h-3.5 text-amber-500" />
                            </div>
                            <div className="text-xl font-bold font-mono text-foreground">Integrated</div>
                            <div className="text-[11px] text-amber-600 dark:text-amber-400 font-medium mt-0.5">I4C Cybercrime &amp; FIR Dockets</div>
                        </div>
                    </div>
                </div>

                <div className="relative z-10 w-full pt-10 pb-4 space-y-12">
                    <LandingWorkbenchDemo isDark={isDarkTheme} />
                    <LandingCaseMap />
                </div>

                {/* Core Capabilities — full viewport width; Workbench-matched LE cards */}
                <div className="relative z-10 w-full pt-6 px-3 sm:px-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-left">
                    <div className="p-4 rounded-xl border border-border/60 bg-card/50">
                        <Network className="w-5 h-5 text-primary mb-2" />
                        <h3 className="text-xs font-semibold text-foreground">Graph Workbench</h3>
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Interactive multi-hop fund flow visualization and automated peel chain tracking.
                        </p>
                    </div>
                    <div className="p-4 rounded-xl border border-border/60 bg-card/50">
                        <Waypoints className="w-5 h-5 text-primary mb-2" />
                        <h3 className="text-xs font-semibold text-foreground">Fusion Canvas</h3>
                        <p className="text-[11px] text-muted-foreground mt-1">
                            One case graph across banking, crypto, and social — join identifiers and run operators on the fused surface.
                        </p>
                    </div>
                    <div className="p-4 rounded-xl border border-border/60 bg-card/50">
                        <Zap className="w-5 h-5 text-blue-500 mb-2" />
                        <h3 className="text-xs font-semibold text-foreground">UPI &amp; P2P Resolution</h3>
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Correlate Indian banking, merchant references, and UPI IDs directly to crypto wallets.
                        </p>
                    </div>
                    <div className="p-4 rounded-xl border border-border/60 bg-card/50">
                        <FileText className="w-5 h-5 text-emerald-500 mb-2" />
                        <h3 className="text-xs font-semibold text-foreground">Docket &amp; Notice Export</h3>
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Generate structured annexures and exchange freezing notices directly from graphs.
                        </p>
                    </div>
                </div>
            </main>

            <p className="relative z-20 px-6 pb-2 text-center text-xs text-muted-foreground">
                Authorized Law Enforcement Access Only
            </p>
            <OutrisSiteFooter />
        </div>
    );
}
